import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Input, Spinner, Text } from '@chakra-ui/react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ClockCounterClockwise, Desktop, X } from 'phosphor-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  acessoApi,
  horaDoLog,
  ILogAcesso,
  iniciais,
  quandoFoi,
  solicitanteDoDelegado,
  TipoEvento,
  tipoDoEvento,
  tituloDoDia,
  diaDoLog,
} from './acessoApi';
import { HistoricoDePermissoes } from './HistoricoDePermissoes';
import {
  AlternarDois,
  AZUL,
  Busca,
  Cartao,
  Etiqueta,
  Eyebrow,
  MONO,
  Pilula,
  TEXTO,
  TEXTO_SUAVE,
} from './ui';

const EVENTO: Record<
  TipoEvento,
  { rotulo: string; tom: 'info' | 'aviso' | 'sucesso' | 'neutro' | 'perigo' }
> = {
  login: { rotulo: 'Entrou', tom: 'info' },
  'login-cliente': { rotulo: 'Entrou na Área VIP', tom: 'sucesso' },
  delegado: { rotulo: 'Acesso delegado', tom: 'aviso' },
  saida: { rotulo: 'Saiu', tom: 'neutro' },
  token: { rotulo: 'Renovou a sessão', tom: 'neutro' },
  outro: { rotulo: 'Evento', tom: 'neutro' },
};

/** Filtros por tipo: a API só filtra por trecho da descrição. */
const FILTROS: { valor: string; rotulo: string; descricao?: string }[] = [
  { valor: 'entradas', rotulo: 'Entradas', descricao: 'Login' },
  { valor: 'delegado', rotulo: 'Acesso delegado', descricao: 'Acesso Delegado' },
  { valor: 'saidas', rotulo: 'Saídas', descricao: 'Logout' },
  { valor: 'tudo', rotulo: 'Tudo' },
];

const PERIODOS: { valor: string; rotulo: string; dias?: number }[] = [
  { valor: '7', rotulo: '7 dias', dias: 7 },
  { valor: '30', rotulo: '30 dias', dias: 30 },
  { valor: '90', rotulo: '90 dias', dias: 90 },
  { valor: 'intervalo', rotulo: 'Escolher datas' },
  { valor: 'tudo', rotulo: 'Desde o início' },
];

/** Início e fim do período, em horário de Brasília (sem horário de verão desde 2019). */
function limitesDoPeriodo(periodo: string, de: string | null, ate: string | null) {
  const p = PERIODOS.find(x => x.valor === periodo);
  if (p?.dias) return { inicio: new Date(Date.now() - p.dias * 864e5), fim: null };
  if (periodo === 'intervalo')
    return {
      inicio: de ? new Date(`${de}T00:00:00-03:00`) : null,
      fim: ate ? new Date(`${ate}T23:59:59.999-03:00`) : null,
    };
  return { inicio: null, fim: null };
}

const aparelho = (l: ILogAcesso) =>
  [l.navegador, l.sistema_operacional, l.dispositivo].filter(v => v && v !== 'Other').join(' · ');

function LinhaDoLog({
  log,
  onFiltrarUsuario,
}: {
  log: ILogAcesso;
  onFiltrarUsuario?: (log: ILogAcesso) => void;
}) {
  const tipo = tipoDoEvento(log);
  const solicitante = tipo === 'delegado' ? solicitanteDoDelegado(log) : null;
  return (
    <Flex
      align="center"
      gap="14px"
      p="10px 20px"
      borderTop="1px solid rgba(15,23,42,.05)"
      wrap="wrap"
      bg={tipo === 'delegado' ? 'rgba(254,243,199,.35)' : undefined}
    >
      <Text fontFamily={MONO} fontSize="12px" color={TEXTO_SUAVE} w="44px" flex="none">
        {horaDoLog(log.dt_criacao)}
      </Text>
      <Flex
        as="button"
        type="button"
        disabled={!onFiltrarUsuario}
        cursor={onFiltrarUsuario ? 'pointer' : 'default'}
        onClick={onFiltrarUsuario ? () => onFiltrarUsuario(log) : undefined}
        title={onFiltrarUsuario ? `Ver só ${log.usuario_nome}` : undefined}
        align="center"
        gap="10px"
        flex="1 1 240px"
        minW={0}
        textAlign="left"
        _hover={onFiltrarUsuario ? { '& .nome': { color: AZUL } } : undefined}
      >
        <Flex
          w="32px"
          h="32px"
          borderRadius="50%"
          bg="#DFF3FF"
          color="#075985"
          align="center"
          justify="center"
          fontSize="11.5px"
          fontWeight={700}
          flex="none"
        >
          {iniciais(log.usuario_nome)}
        </Flex>
        <Box minW={0}>
          <Text className="nome" fontSize="13.5px" fontWeight={600} color={TEXTO} noOfLines={1}>
            {log.usuario_nome}
          </Text>
          <Text fontSize="12px" color={TEXTO_SUAVE} noOfLines={1}>
            {log.usuario_email}
          </Text>
        </Box>
      </Flex>
      <Box flex="0 1 200px" minW={0}>
        <Etiqueta tom={EVENTO[tipo].tom}>{EVENTO[tipo].rotulo}</Etiqueta>
        {solicitante && (
          <Text fontSize="11.5px" color="#92400E" mt="2px" noOfLines={1}>
            pedido por {solicitante}
          </Text>
        )}
      </Box>
      <Flex
        align="center"
        gap="6px"
        flex="0 1 220px"
        minW={0}
        fontSize="12px"
        color={TEXTO_SUAVE}
        display={{ base: 'none', md: 'flex' }}
      >
        <Desktop size={14} />
        <Text noOfLines={1}>{aparelho(log) || 'aparelho não identificado'}</Text>
      </Flex>
      <Text fontFamily={MONO} fontSize="12px" color="#334155" flex="0 0 120px" textAlign="right">
        {log.endereco_ip ?? '—'}
      </Text>
    </Flex>
  );
}

/** Aba "Logs de acesso": entradas e saídas, ou o histórico de mudanças de permissão. */
export function LogsDeAcesso() {
  const [params, setParams] = useSearchParams();
  const visao = params.get('visao') === 'permissoes' ? 'permissoes' : 'acessos';
  return (
    <Flex direction="column" gap="16px">
      <Box maxW="460px">
        <AlternarDois
          valor={visao}
          onChange={v => {
            // cada visão tem os próprios filtros na URL
            const p = new URLSearchParams();
            if (v === 'permissoes') p.set('visao', 'permissoes');
            setParams(p, { replace: true });
          }}
          opcoes={[
            { valor: 'acessos', rotulo: 'Entradas e saídas' },
            { valor: 'permissoes', rotulo: 'Mudanças de permissão' },
          ]}
        />
      </Box>
      {visao === 'permissoes' ? <HistoricoDePermissoes /> : <EntradasESaidas />}
    </Flex>
  );
}

/** Quem entrou, saiu ou usou acesso delegado, por dia. */
function EntradasESaidas() {
  const [params, setParams] = useSearchParams();
  const usuarioId = Number(params.get('usuario')) || undefined;
  const usuarioNome = params.get('nome');
  const filtro = FILTROS.find(f => f.valor === params.get('tipo')) ?? FILTROS[0];
  const periodo = PERIODOS.find(x => x.valor === params.get('periodo'))?.valor ?? '30';
  const de = params.get('de');
  const ate = params.get('ate');
  // hora cheia: "últimos 7 dias" não troca a chave do cache a cada render
  const [hora] = useState(() => Math.floor(Date.now() / 36e5));
  const { inicio, fim } = useMemo(
    () => limitesDoPeriodo(periodo, de, ate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [periodo, de, ate, hora],
  );
  const intervaloInvertido = !!(inicio && fim && inicio > fim);

  const [digitado, setDigitado] = useState('');
  const [busca, setBusca] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBusca(digitado.trim()), 400);
    return () => clearTimeout(t);
  }, [digitado]);

  const mudar = (novo: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    Object.entries(novo).forEach(([k, v]) => (v === null ? p.delete(k) : p.set(k, v)));
    setParams(p, { replace: true });
  };

  const consulta = useInfiniteQuery({
    queryKey: [
      'central',
      'logs',
      { busca, usuarioId, descricao: filtro.descricao, periodo, de, ate, hora },
    ],
    queryFn: ({ pageParam }) =>
      acessoApi.logs(
        {
          search: busca,
          usuario_id: usuarioId,
          descricao: filtro.descricao,
          data_inicio: inicio?.toISOString(),
          data_fim: fim?.toISOString(),
        },
        pageParam,
      ),
    enabled: !intervaloInvertido,
    initialPageParam: 1,
    getNextPageParam: ultima => ultima.next_page ?? undefined,
  });
  const carregados = useMemo(
    () => (consulta.data?.pages ?? []).flatMap(p => p.items),
    [consulta.data],
  );
  // enquanto o backend não filtra por data, ele devolve registros fora do período: a tela
  // filtra o que já veio e o total da API deixa de valer
  const { logs, semFiltroNoBackend } = useMemo(() => {
    const dentro = (l: ILogAcesso) => {
      const d = new Date(l.dt_criacao);
      return (!inicio || d >= inicio) && (!fim || d <= fim);
    };
    const fora = carregados.some(l => !dentro(l));
    return { logs: fora ? carregados.filter(dentro) : carregados, semFiltroNoBackend: fora };
  }, [carregados, inicio, fim]);
  const total = semFiltroNoBackend ? logs.length : (consulta.data?.pages[0]?.total ?? 0);
  // a lista vem do mais novo para o mais antigo: passou do início, não há mais nada no período
  const passouDoInicio =
    !!inicio &&
    carregados.length > 0 &&
    new Date(carregados[carregados.length - 1].dt_criacao) < inicio;
  const temMais = !!consulta.hasNextPage && !passouDoInicio;

  const porDia = useMemo(() => {
    const dias: { dia: string; titulo: string; itens: ILogAcesso[] }[] = [];
    logs.forEach(l => {
      const dia = diaDoLog(l.dt_criacao);
      const atual = dias[dias.length - 1];
      if (atual && atual.dia === dia) atual.itens.push(l);
      else dias.push({ dia, titulo: tituloDoDia(l.dt_criacao), itens: [l] });
    });
    return dias;
  }, [logs]);

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="18px 20px" display="flex" flexDirection="column" gap="14px">
        <Flex align="flex-start" justify="space-between" gap="12px" wrap="wrap">
          <Box>
            <Eyebrow>LOGS DE ACESSO</Eyebrow>
            <Text fontSize="22px" lineHeight="28px" fontWeight={700} letterSpacing="-.3px">
              Quem entrou no sistema
            </Text>
            <Text fontSize="13px" color="#475569">
              Entradas, saídas e acessos delegados, do mais recente para o mais antigo. Clique numa
              pessoa para ver só os acessos dela.
            </Text>
          </Box>
          <Box textAlign="right">
            <Text fontFamily={MONO} fontSize="22px" fontWeight={600} color={TEXTO}>
              {consulta.isLoading ? '…' : total.toLocaleString('pt-BR')}
            </Text>
            <Text fontSize="12px" color={TEXTO_SUAVE}>
              {semFiltroNoBackend ? 'carregados no período' : 'registros neste filtro'}
            </Text>
          </Box>
        </Flex>
        <Flex gap="10px" wrap="wrap" align="center">
          <Box flex="1 1 280px" maxW="420px">
            <Busca
              valor={digitado}
              onChange={setDigitado}
              placeholder="Buscar por nome, e-mail ou IP"
            />
          </Box>
          <Flex gap="6px" wrap="wrap">
            {FILTROS.map(f => (
              <Pilula
                key={f.valor}
                ativa={f.valor === filtro.valor}
                onClick={() => mudar({ tipo: f.valor === 'entradas' ? null : f.valor })}
              >
                {f.rotulo}
              </Pilula>
            ))}
          </Flex>
          {usuarioId && (
            <Flex
              align="center"
              gap="6px"
              h="34px"
              pl="12px"
              pr="6px"
              borderRadius="999px"
              bg="#DFF3FF"
              color="#075985"
              fontSize="13px"
              fontWeight={600}
            >
              Só {usuarioNome ?? `usuário ${usuarioId}`}
              <Box
                as="button"
                type="button"
                aria-label="Ver todas as pessoas"
                onClick={() => mudar({ usuario: null, nome: null })}
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="22px"
                h="22px"
                borderRadius="50%"
                _hover={{ bg: 'rgba(7,89,133,.12)' }}
              >
                <X size={12} />
              </Box>
            </Flex>
          )}
        </Flex>
        <Flex gap="10px" wrap="wrap" align="center">
          <Eyebrow>PERÍODO</Eyebrow>
          <Flex gap="6px" wrap="wrap">
            {PERIODOS.map(p => (
              <Pilula
                key={p.valor}
                ativa={p.valor === periodo}
                onClick={() =>
                  mudar({
                    periodo: p.valor === '30' ? null : p.valor,
                    ...(p.valor === 'intervalo' ? {} : { de: null, ate: null }),
                  })
                }
              >
                {p.rotulo}
              </Pilula>
            ))}
          </Flex>
          {periodo === 'intervalo' && (
            <Flex gap="8px" align="center" wrap="wrap">
              <Input
                type="date"
                size="sm"
                w="160px"
                h="34px"
                borderRadius="10px"
                bg="white"
                aria-label="De"
                value={de ?? ''}
                max={ate ?? undefined}
                onChange={e => mudar({ de: e.target.value || null })}
              />
              <Text fontSize="13px" color={TEXTO_SUAVE}>
                até
              </Text>
              <Input
                type="date"
                size="sm"
                w="160px"
                h="34px"
                borderRadius="10px"
                bg="white"
                aria-label="Até"
                value={ate ?? ''}
                min={de ?? undefined}
                onChange={e => mudar({ ate: e.target.value || null })}
              />
            </Flex>
          )}
        </Flex>
        {intervaloInvertido && (
          <Text fontSize="12px" color="#991B1B">
            A data inicial está depois da final.
          </Text>
        )}
        {semFiltroNoBackend && (
          <Text
            fontSize="12.5px"
            color="#92400E"
            bg="rgba(254,243,199,.6)"
            p="8px 12px"
            borderRadius="12px"
          >
            Pré-visualização: o backend desta versão ainda não filtra por data. A tela mostra só os
            registros já carregados que caem no período
            {fim ? '; num intervalo antigo pode ser preciso carregar muitas páginas' : ''}.
          </Text>
        )}
        {filtro.valor === 'tudo' && (
          <Text fontSize="12px" color={TEXTO_SUAVE}>
            &quot;Tudo&quot; inclui as renovações automáticas de sessão, que acontecem enquanto a
            pessoa está com o sistema aberto.
          </Text>
        )}
      </Cartao>

      <Cartao p={0} overflow="hidden">
        {consulta.isLoading && (
          <Flex justify="center" py={10}>
            <Spinner color={AZUL} />
          </Flex>
        )}
        {consulta.isError && (
          <Text p="20px" fontSize="13px" color="#991B1B">
            Não foi possível carregar os logs de acesso.
          </Text>
        )}
        {!consulta.isLoading && !consulta.isError && logs.length === 0 && temMais && (
          <Flex justify="center" py={6}>
            <Button
              size="sm"
              variant="outline"
              borderRadius="999px"
              bg="white"
              isLoading={consulta.isFetchingNextPage}
              onClick={() => consulta.fetchNextPage()}
            >
              Nada do período nesta página: carregar mais
            </Button>
          </Flex>
        )}
        {!consulta.isLoading && !consulta.isError && logs.length === 0 && !temMais && (
          <Flex direction="column" align="center" gap="8px" py={12} color={TEXTO_SUAVE}>
            <ClockCounterClockwise size={32} />
            <Text fontSize="13px">Nenhum acesso com esse filtro.</Text>
          </Flex>
        )}
        {porDia.map(d => (
          <Box key={d.dia}>
            <Flex
              align="center"
              justify="space-between"
              p="12px 20px 8px"
              bg="rgba(248,250,252,.7)"
              position="sticky"
              top={0}
              zIndex={1}
            >
              <Text fontSize="12.5px" fontWeight={700} color="#334155">
                {d.titulo}
              </Text>
              <Text fontFamily={MONO} fontSize="11px" color={TEXTO_SUAVE}>
                {d.itens.length}
              </Text>
            </Flex>
            {d.itens.map(l => (
              <LinhaDoLog
                key={l.id}
                log={l}
                onFiltrarUsuario={
                  usuarioId
                    ? undefined
                    : x => mudar({ usuario: String(x.usuario_id), nome: x.usuario_nome })
                }
              />
            ))}
          </Box>
        ))}
        {logs.length > 0 && (
          <Flex
            align="center"
            justify="space-between"
            gap="12px"
            p="12px 20px"
            borderTop="1px solid rgba(15,23,42,.06)"
            wrap="wrap"
          >
            <Text fontSize="12.5px" color={TEXTO_SUAVE}>
              {semFiltroNoBackend
                ? `Mostrando ${logs.length.toLocaleString('pt-BR')} do período`
                : `Mostrando ${logs.length.toLocaleString('pt-BR')} de ${total.toLocaleString(
                    'pt-BR',
                  )}`}
            </Text>
            {temMais && (
              <Button
                size="sm"
                variant="outline"
                borderRadius="999px"
                bg="white"
                isLoading={consulta.isFetchingNextPage}
                onClick={() => consulta.fetchNextPage()}
              >
                Carregar mais
              </Button>
            )}
          </Flex>
        )}
      </Cartao>
    </Flex>
  );
}

/** Últimas entradas de uma pessoa, para o painel do usuário e do cliente. */
export function UltimosAcessos({ usuarioId, nome }: { usuarioId: number; nome: string }) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['central', 'logs', 'ultimos', usuarioId],
    queryFn: () => acessoApi.logs({ usuario_id: usuarioId, descricao: 'Login' }, 1, 5),
  });
  const itens = data?.items ?? [];

  return (
    <Flex direction="column" gap="10px">
      <Flex justify="space-between" align="center" gap="10px" wrap="wrap">
        <Eyebrow>ÚLTIMOS ACESSOS</Eyebrow>
        {itens.length > 0 && (
          <Button
            size="xs"
            variant="link"
            color={AZUL}
            onClick={() =>
              navigate(
                `/configuracoes/logs-de-acesso?${new URLSearchParams({
                  usuario: String(usuarioId),
                  nome,
                })}`,
              )
            }
          >
            Ver todos ({(data?.total ?? 0).toLocaleString('pt-BR')})
          </Button>
        )}
      </Flex>
      {isLoading && <Spinner size="sm" color={AZUL} />}
      {isError && (
        <Text fontSize="12.5px" color={TEXTO_SUAVE}>
          Não foi possível carregar os acessos.
        </Text>
      )}
      {!isLoading && !isError && itens.length === 0 && (
        <Text fontSize="13px" color={TEXTO_SUAVE}>
          Nenhuma entrada registrada.
        </Text>
      )}
      {itens.map(l => {
        const tipo = tipoDoEvento(l);
        return (
          <Flex key={l.id} align="center" gap="10px" fontSize="12.5px" wrap="wrap">
            <Text color={TEXTO} fontWeight={600} w="120px" flex="none">
              {quandoFoi(l.dt_criacao)}
            </Text>
            <Etiqueta tom={EVENTO[tipo].tom}>{EVENTO[tipo].rotulo}</Etiqueta>
            <Text color={TEXTO_SUAVE} flex="1 1 140px" noOfLines={1}>
              {aparelho(l)}
            </Text>
            <Text fontFamily={MONO} fontSize="11.5px" color="#334155">
              {l.endereco_ip}
            </Text>
          </Flex>
        );
      })}
    </Flex>
  );
}
